import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TWButton } from '../../../../components/ui';
import connectApi from '../../../../services/connectApi';
import { apiMessage } from '../../../connect/connect-api-message';
import {
  INTERVALS, MONTH_DAYS, WEEKDAYS, bodyOf, changedFrom, formOf,
} from '../../../connect/finance/payout-schedule-model';

// Support's view of a restaurant's payout schedule, through PUT
// Connect/PayoutSchedule. Since 2026-10-20 (spec
// 2026-10-20-payout-schedule-everywhere) the restaurant edits the same
// schedule itself — Overview's modal, Stripe's own card on Payouts, the
// Manager app — so this form is one of several editors of one Stripe
// setting; each reads Stripe fresh, and the lists, the form shape and the
// body live in payout-schedule-model.js so the two web editors cannot drift.
//
// Collapsed like Settlement and Support beside it, for the same reasons:
// the current schedule comes from GET Connect/Balance, a read of the
// restaurant's Stripe account that the tab makes only once an admin asks,
// and nothing is drawn at all for an account Stripe has not finished
// checking — connect-tab.test.js pins "no money surface" for that state.
const weekdayLabel = (value) => WEEKDAYS.find((d) => d.value === value)?.label;

// The schedule as an admin needs to read it, anchor included — the
// restaurant-facing describeSchedule stops at "Weekly", which is not enough
// to decide whether to change it.
const describeAdminSchedule = (balance) => {
  const interval = balance?.scheduleInterval;
  // The restaurant can still pay out by hand from Stripe's balances card;
  // nothing leaves by itself.
  if (interval === 'manual') return 'Manual · paid out on request';
  if (interval === 'daily') {
    const days = balance.scheduleDelayDays;
    return typeof days === 'number'
      ? `Daily · arrives in ${days} business ${days === 1 ? 'day' : 'days'}`
      : 'Daily';
  }
  if (interval === 'weekly') {
    const day = weekdayLabel(balance.scheduleWeeklyAnchor);
    return day ? `Weekly on ${day}` : 'Weekly';
  }
  if (interval === 'monthly') {
    const day = balance.scheduleMonthlyAnchor;
    return typeof day === 'number' ? `Monthly on day ${day}` : 'Monthly';
  }
  return 'Not set';
};

const Label = ({ children }) => (
  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
    {children}
  </p>
);

const SELECT_CLS =
  'rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';

const ConnectPayoutSchedule = ({ companyId, hasAccount, isReady, onNotify }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ balance: null, error: '', loading: false });
  const [form, setForm] = useState(formOf(null));
  const [saving, setSaving] = useState(false);
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const notify = useCallback((type, message) => onNotify?.(type, message), [onNotify]);

  const load = useCallback(async () => {
    if (!companyId) return;
    requestId.current += 1;
    const id = requestId.current;
    setState((prev) => ({ ...prev, error: '', loading: true }));
    try {
      const next = connectApi.toConnectBalance(await connectApi.getConnectBalance(companyId));
      if (!mounted.current || id !== requestId.current) return;
      if (!next) throw new Error('empty');
      setState({ balance: next, error: '', loading: false });
      setForm(formOf(next));
    } catch (caught) {
      if (!mounted.current || id !== requestId.current) return;
      const reason = apiMessage(caught);
      setState((prev) => ({
        ...prev,
        error: reason ? `Couldn't read the payout schedule — ${reason}` : "Couldn't read the payout schedule",
        loading: false,
      }));
    }
  }, [companyId]);

  useEffect(() => {
    if (open && hasAccount && isReady) load();
  }, [open, hasAccount, isReady, load]);

  const changed = changedFrom(form, state.balance);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    if (saving || !changed) return;
    setSaving(true);
    try {
      const res = await connectApi.updatePayoutSchedule(companyId, bodyOf(form));
      if (!mounted.current) return;
      notify('success', 'Payout schedule saved');
      // The API answers with the balance it re-read after the save; a null
      // body means the save stood but that read failed, so read it here.
      const next = connectApi.toConnectBalance(res);
      if (next) {
        setState({ balance: next, error: '', loading: false });
        setForm(formOf(next));
      } else {
        await load();
      }
    } catch (caught) {
      // Stripe's own reason (a schedule this account's country refuses) or
      // the API's validation sentence, sanitised; the admin's choice stays
      // on screen so it can be adjusted rather than re-entered.
      notify('error', apiMessage(caught) || "Couldn't save the payout schedule");
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  if (!hasAccount || !isReady) return null;

  return (
    <section className="py-6">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Payout schedule</h3>
      <p className="mt-3 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
        How often Stripe sends this restaurant's balance to its bank. The
        restaurant can change it too, from Overview, Payouts and the Manager
        app; this form is for support.
      </p>
      <div className="mt-4">
        <TWButton
          size="sm"
          label={open ? 'Hide payout schedule' : 'Show payout schedule'}
          onClick={() => setOpen((prev) => !prev)}
        />
      </div>

      {open ? (
        <div className="mt-4 max-w-2xl space-y-5">
          {state.error ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-rose-600 dark:text-rose-400">{state.error}</p>
              <TWButton size="sm" label="Try again" onClick={load} />
            </div>
          ) : !state.balance ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          ) : (
            <>
              <div>
                <Label>Current schedule</Label>
                <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {describeAdminSchedule(state.balance)}
                </p>
              </div>

              <fieldset className="space-y-2">
                <legend className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Change to
                </legend>
                {INTERVALS.map((option) => (
                  <div key={option.value} className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={`payout-schedule-${companyId}`}
                        checked={form.interval === option.value}
                        onChange={() => set({ interval: option.value })}
                      />
                      <span className="text-slate-700 dark:text-slate-200">{option.label}</span>
                    </label>
                    {option.value === 'weekly' && form.interval === 'weekly' ? (
                      <select
                        aria-label="Day of the week"
                        className={SELECT_CLS}
                        value={form.weeklyAnchor}
                        onChange={(e) => set({ weeklyAnchor: e.target.value })}
                      >
                        {WEEKDAYS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    ) : null}
                    {option.value === 'monthly' && form.interval === 'monthly' ? (
                      <select
                        aria-label="Day of the month"
                        className={SELECT_CLS}
                        value={form.monthlyAnchor}
                        onChange={(e) => set({ monthlyAnchor: Number(e.target.value) })}
                      >
                        {MONTH_DAYS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                ))}
              </fieldset>

              <TWButton
                variant="primary"
                label="Save schedule"
                disabled={!changed || state.loading}
                loading={saving}
                onClick={save}
              />
            </>
          )}
        </div>
      ) : null}
    </section>
  );
};

export default ConnectPayoutSchedule;
